/*
 *  Header Navigation
 *    Scripts for the header navigation
 *
 *  Version:
 *    1.0.0 - 2020/10/19
 */

import { delegate } from '../../tools/utilities/event-tools';
import {
  CLASS_SUBMENU_OPEN,
  CLASS_SUBSUBMENU_OPEN,
  CLASS_HIDDEN,
  CLASS_ALT,
  CLASS_ACTIVE,
  CLASS_SELECTED,
  CLASS_MENU_TOGGLE,
  CLASS_SCROLL_DISABLED,
} from '../../tools/constants/classes';
import {
  responsiveMediaAbove,
  responsiveMediaBelow,
} from '../../settings/responsive';

// Selectors
const SELECTOR_HEADER = '[data-header]';
const SELECTOR_SUBMENU_CONTAINER = '[data-submenu-container]';
const SELECTOR_MOBILE_SUBMENU_TOGGLE = '[data-mobile-submenu-toggle]';
const SELECTOR_SUBSUBMENU_CONTAINER = '[data-subsubmenu-container]';
const SELECTOR_NOSUBSUBMENU_CONTAINER = '[data-nosubsubmenu-container]';
const SELECTOR_NESTED_CONTAINER = '[data-nested-container]';
const SELECTOR_MOBILE_SUBSUBMENU_TOGGLE = '[data-mobile-subsubmenu-toggle]';
const SELECTOR_MOBILE_BACK = '[data-mobile-submenu-back]';
const SELECTOR_MOBILE_SEARCH = '[data-mobile-search-trigger]';
const SELECTOR_SEARCH_INPUT = '[data-search-input]';

// Global Variable
// keep track of last nested link hovered
let lastNestedLink = document.querySelector<HTMLElement>(
  SELECTOR_SUBSUBMENU_CONTAINER
);

// Functions
const onMouseOver = (element: HTMLElement) => {
  if (responsiveMediaBelow('MEDIUM')) return;
  const header = element.closest(SELECTOR_HEADER);
  const subSubmenu = element.closest(SELECTOR_NESTED_CONTAINER);

  if (!header) return;

  header.classList.add(CLASS_ALT);

  if (subSubmenu) {
    subSubmenu.classList.add(CLASS_SUBMENU_OPEN);
  } else {
    element.classList.add(CLASS_SUBMENU_OPEN);
  }
};

const onMouseLeave = (element: HTMLElement) => {
  if (responsiveMediaBelow('MEDIUM')) return;
  const header = element.closest(SELECTOR_HEADER);
  const subSubmenu = element.closest(SELECTOR_NESTED_CONTAINER);
  const nestedLink = subSubmenu?.querySelector<HTMLElement>(
    SELECTOR_SUBSUBMENU_CONTAINER
  );

  if (!header) return;

  header.classList.remove(CLASS_ALT);

  if (subSubmenu) {
    subSubmenu.classList.remove(CLASS_SUBMENU_OPEN);
    // keep the first nested link active if it's not the lastNestedLink
    if (nestedLink === lastNestedLink) {
      nestedLink?.classList.add(CLASS_ACTIVE);
    }
  } else {
    element.classList.remove(CLASS_SUBMENU_OPEN);
  }
};

// Last hovered Submenu item stays hovered until hover on something else
const onMouseMove = () => {
  if (responsiveMediaBelow('MEDIUM')) return;
  if (!lastNestedLink?.classList.contains(CLASS_ACTIVE)) {
    lastNestedLink?.classList.add(CLASS_ACTIVE);
  }
};

const onSubmenuToggle = (evt: Event) => {
  // Don't run on desktop
  if (responsiveMediaAbove('MEDIUM')) return;

  evt.preventDefault();

  const target = evt.target as HTMLElement;
  const submenu = target.closest(SELECTOR_SUBMENU_CONTAINER);

  // Check submenu exists
  if (submenu === null) return;

  // Open / Close
  submenu.classList.toggle(CLASS_SUBMENU_OPEN);
};

const onSubSubmenuToggle = (evt: Event) => {
  // Don't run on desktop
  if (responsiveMediaAbove('MEDIUM')) return;

  const target = evt.target as HTMLElement;
  const subsubmenu = target.closest(
    SELECTOR_SUBSUBMENU_CONTAINER
  ) as HTMLElement;
  const submenuButton =
    document.querySelector<HTMLElement>(SELECTOR_MOBILE_BACK);

  evt.preventDefault();

  // Check subsubmenu exists
  if (subsubmenu === null) return;

  // Already open? Close
  if (subsubmenu.classList.contains(CLASS_SUBSUBMENU_OPEN)) {
    subsubmenu.classList.remove(CLASS_SUBSUBMENU_OPEN);
    submenuButton?.classList.remove(CLASS_HIDDEN);
    return;
  }

  // Open subsubmenu
  subsubmenu.classList.add(CLASS_SUBSUBMENU_OPEN);

  // Hide submenu button
  submenuButton?.classList.add(CLASS_HIDDEN);
};

const onResize = () => {
  if (responsiveMediaAbove('MEDIUM')) {
    // Close all submenus
    const submenus = document.querySelectorAll<HTMLElement>(
      SELECTOR_SUBSUBMENU_CONTAINER
    );
    submenus.forEach((submenu) => {
      submenu.classList.remove(CLASS_SUBSUBMENU_OPEN);
    });
    document.body.classList.remove(CLASS_MENU_TOGGLE, CLASS_SCROLL_DISABLED);
  }
};

// When input field has a value, clicking on the search trigger will submit the form
const onSubmit = (evt: Event) => {
  // Don't run on desktop
  if (responsiveMediaAbove('MEDIUM')) return;

  evt.preventDefault();

  const searchInput = document.querySelector<HTMLInputElement>(
    SELECTOR_SEARCH_INPUT
  );

  // Check search input exists
  if (searchInput === null) return;

  // Check search input has a value
  if (searchInput.value) {
    // Submit form
    searchInput.form?.submit();
  } else {
    searchInput.focus();
    searchInput.select();
  }
};

/*
  Events
*/
// Menu overlay hover events
// Main nav hover - Shop, Learn, Support
document
  .querySelectorAll<HTMLElement>(SELECTOR_SUBMENU_CONTAINER)
  .forEach((element) => {
    element.addEventListener('mouseover', () => onMouseOver(element));
    element.addEventListener('mouseleave', () => onMouseLeave(element));
    element.addEventListener('mousemove', () => onMouseMove());
  });

// Submenu hover with nested links - Tools, Skincare
document
  .querySelectorAll<HTMLElement>(SELECTOR_SUBSUBMENU_CONTAINER)
  .forEach((element) => {
    element.addEventListener('mouseover', () => {
      if (responsiveMediaBelow('MEDIUM')) return;
      element.classList.add(CLASS_ACTIVE);
      if (lastNestedLink !== element) {
        lastNestedLink?.classList.remove(CLASS_ACTIVE);
        lastNestedLink = element;
      }
    });
    element.addEventListener('mouseleave', () => {
      if (responsiveMediaBelow('MEDIUM')) return;
      element.classList.remove(CLASS_ACTIVE);
    });
  });
// Submenu hover without nested links - Shop All will open a hover menu of the previous nested link
document
  .querySelectorAll<HTMLElement>(SELECTOR_NOSUBSUBMENU_CONTAINER)
  .forEach((noSubElement) => {
    noSubElement.addEventListener('mouseover', () => {
      if (responsiveMediaBelow('MEDIUM')) return;
      lastNestedLink?.classList.add(CLASS_SELECTED);
      if (lastNestedLink?.classList.contains(CLASS_ACTIVE)) {
        lastNestedLink?.classList.remove(CLASS_ACTIVE);
      }
    });
    noSubElement.addEventListener('mouseleave', () => {
      if (responsiveMediaBelow('MEDIUM')) return;
      lastNestedLink?.classList.remove(CLASS_SELECTED);
    });
  });

document
  .querySelectorAll<HTMLElement>(SELECTOR_MOBILE_SEARCH)
  .forEach((element) => {
    element.addEventListener('click', () => {
      const searchInput = document.querySelector<HTMLInputElement>(
        SELECTOR_SEARCH_INPUT
      );
      if (!searchInput) return;
      searchInput.focus();
      searchInput.select();
    });
  });

// On Resize close all mobile Menu
window.addEventListener('resize', onResize);

// On submenu trigger click
delegate({
  element: document.documentElement,
  selector: SELECTOR_MOBILE_SUBMENU_TOGGLE,
  event: 'click',
  callback: onSubmenuToggle,
});

// On subsubmenu trigger click
delegate({
  element: document.documentElement,
  selector: SELECTOR_MOBILE_SUBSUBMENU_TOGGLE,
  event: 'click',
  callback: onSubSubmenuToggle,
});

// On search trigger click
delegate({
  element: document.documentElement,
  selector: SELECTOR_MOBILE_SEARCH,
  event: 'click',
  callback: onSubmit,
});
